import { Product } from '@shared/components/ui/Product';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { mapOrdersToModalList, ProductsWithoutStockModel } from '../../models/ProductsWithoutStock';
import { OrdersWithProductModal, OrdersWithProductModalRef } from '../modals/OrdersWithProductModal';
import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<ProductsWithoutStockModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
}

// 🎯 Componente de card individual para ProductsWithoutStock - SRP
export const Card = ({ item: product, className = '' }: CardProps) => {
  const { t } = useTranslation('productsWithoutStockModule');
  const ordersModalRef = useRef<OrdersWithProductModalRef>(null);

  // 🎯 Función para abrir el modal de pedidos
  const handleOrderClick = () => {
    // 🎯 Usar datos reales de la API
    const orders = product.orders ?? [];
    const orderData = {
      shop: {
        name: product.shop_name,
      },
      orders: mapOrdersToModalList(orders),
    };

    ordersModalRef.current?.openModal(orderData);
  };

  // 🎯 Validación de datos para evitar errores
  if (!product) {
    return (
      <div className={`py-4 border-b border-card-border ${className}`}>
        <div className="text-center text-subtext">{t('fallbacks.no_product')}</div>
      </div>
    );
  }

  return (
    <div
      className={`bg-card-bg border border-card-border rounded-lg p-4 hover:bg-hover-press transition-colors mb-4 ${className}`}
    >
      <div className="space-y-3">
        {/* 🎯 Header con producto y pedido */}
        <div className="flex items-center justify-between gap-3">
          {/* 🎯 Información del producto */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Product
              id={product.product_id}
              name={product.product_name}
              code={product.product_code}
              src={product.product_image_url}
              enableLineClamp={true}
              showTooltip={true}
              showImageModal={true}
              data-testid={`card-product-${product.product_id}`}
            />
          </div>

          {/* 🎯 Contador de pedidos */}
          <div
            className="flex items-center gap-2 bg-body-bg border border-card-border rounded-md px-2 py-2 flex-shrink-0 cursor-pointer hover:bg-hover-press transition-colors"
            onClick={handleOrderClick}
            data-testid={`card-order-click-${product.product_id}`}
          >
            <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3 text-icons" />
            <span className="text-xs font-medium text-title">{(product.orders ?? []).length || 1}</span>
          </div>
        </div>

        {/* 🎯 Métricas de stock en 3 columnas */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="text-center border border-card-border rounded-lg py-1 px-3">
            <div className="text-xs mb-1">{t('table.required')}</div>
            <div className="font-medium text-title">{product.total_quantity || 0}</div>
          </div>
          <div className="text-center border border-card-border rounded-lg py-1 px-3">
            <div className="text-xs mb-1">{t('table.available')}</div>
            <div className="font-medium text-title">{product.available_stock || 0}</div>
          </div>
          <div className="text-center border border-card-border rounded-lg py-1 px-3">
            <div className="text-xs mb-1">{t('table.deficit')}</div>
            <div className="font-medium text-title">{product.stock_deficit || 0}</div>
          </div>
        </div>
      </div>

      {/* 🎯 Modal de pedidos con producto */}
      <OrdersWithProductModal ref={ordersModalRef} />
    </div>
  );
};
